import { useState, useCallback, useEffect } from 'react';

const SESSION_STORAGE_KEY = 'closedTooltips';

let globalClosedTooltips = null;
const listeners = new Set();

const initializeGlobalState = () => {
  if (globalClosedTooltips === null) {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      globalClosedTooltips = stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.warn('Error reading from sessionStorage:', error);
      globalClosedTooltips = new Set();
    }
  }
  return globalClosedTooltips;
};

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const useTooltipSession = () => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const listener = () => forceUpdate({});
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const closedTooltips = initializeGlobalState();

  const markTooltipClosed = useCallback((tooltipId) => {
    globalClosedTooltips.add(tooltipId);

    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(Array.from(globalClosedTooltips)));
    } catch (error) {
      console.warn('Error writing to sessionStorage:', error);
    }

    notifyListeners();
  }, []);

  const isTooltipClosed = useCallback(
    (tooltipId) => {
      return globalClosedTooltips.has(tooltipId);
    },
    [closedTooltips],
  );

  const resetClosedTooltips = useCallback(() => {
    globalClosedTooltips.clear();
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Error removing from sessionStorage:', error);
    }
    notifyListeners();
  }, []);

  return {
    markTooltipClosed,
    isTooltipClosed,
    resetClosedTooltips,
  };
};

export default useTooltipSession;
